import { useState, useRef } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Check, Loader2, Ruler, Scissors, Truck } from "lucide-react";
import SEO from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import CushionPreview from "@/components/site/CushionPreview";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fileUrl } from "@/lib/api";
import { FABRIC_LINES } from "@/data/site";

const ZIPPER_OPTIONS = ["One-sided zipper", "Three-sided zipper", "Not sure — please advise"];

function Swatch({ color, active, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-sm border p-3 text-left transition-colors ${active ? "border-teal-primary bg-teal-primary/5" : "border-teal-anchor/15 hover:border-teal-anchor/40"}`}
    >
      <span className="h-10 w-10 shrink-0 rounded-sm border border-teal-anchor/15" style={color.swatch ? { backgroundImage: `url(${color.swatch})`, backgroundSize: "cover" } : { backgroundColor: color.color }} />
      <span className="text-sm font-medium leading-tight">{color.label}</span>
    </button>
  );
}

const fabOf = (c) => (c.swatch ? { texture: c.swatch } : { color: c.color });

export default function CoverBuilder() {
  const { fabricId } = useParams();
  return <CoverBuilderInner key={fabricId} fabricId={fabricId} />;
}

function CoverBuilderInner({ fabricId }) {
  const fabric = FABRIC_LINES[fabricId];

  const [topId, setTopId] = useState(fabric?.colors[0]?.id);
  const [sideId, setSideId] = useState(fabric?.colors[0]?.id);
  const [dims, setDims] = useState({ length: "", width: "", height: "" });
  const [additional, setAdditional] = useState("");
  const [piping, setPiping] = useState(false);
  const [zipper, setZipper] = useState(ZIPPER_OPTIONS[0]);
  const [quantity, setQuantity] = useState(1);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", zip: "" });
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [activePhoto, setActivePhoto] = useState(fabric?.photos[0]);
  const fileRef = useRef(null);

  if (!fabric) return <Navigate to="/covers" replace />;

  const topColor = fabric.colors.find((c) => c.id === topId) || fabric.colors[0];
  const sideColor = fabric.colors.find((c) => c.id === sideId) || fabric.colors[0];
  const setDim = (k) => (e) => setDims({ ...dims, [k]: e.target.value.replace(/[^0-9.]/g, "") });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data]);
      } catch { toast.error(`Could not upload ${file.name}.`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!contact.name || !contact.email) { toast.error("Please add your name and email."); return; }
    if (!dims.length || !dims.width) { toast.error("Please enter at least length and width."); return; }
    setSubmitting(true);
    try {
      const dimText = `${dims.length}"L × ${dims.width}"W × ${dims.height || 0}"H` + (additional ? `; ${additional}` : "");
      const fabricPref = fabric.dualTone
        ? `${fabric.name} — Top & Bottom: ${topColor.label}; Sides: ${sideColor.label}`
        : `${fabric.name} — ${topColor.label}`;
      const details = [
        `Fabric: ${fabricPref}`,
        `Piping: ${piping ? "Yes" : "No"}`,
        `Zipper: ${zipper}`,
        contact.zip ? `Ship-to ZIP: ${contact.zip}` : "",
        notes ? `Notes: ${notes}` : "",
      ].filter(Boolean).join(". ");
      await api.post("/custom-orders", {
        name: contact.name, email: contact.email, phone: contact.phone,
        project_type: `${fabric.name} Cover`,
        quantity: String(quantity),
        dimensions: dimText,
        fabric_option: "provided",
        fabric_preference: fabricPref,
        details, photos,
      });
      setDone(true);
      window.scrollTo(0, 0);
    } catch { toast.error("Could not submit your request. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="cover-builder-success" className="mx-auto max-w-2xl px-5 sm:px-8 py-32 text-center">
        <SEO title="Request Received" />
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-primary text-paper"><Check size={30} /></div>
        <h1 className="mt-8 font-serif text-4xl lg:text-5xl tracking-tight">Thank you, {contact.name.split(" ")[0]}.</h1>
        <p className="mt-5 text-lg text-teal-anchor/70 leading-relaxed">
          We've received your {fabric.name} cover request. Every project is personally reviewed — we'll email
          {" "}<span className="font-medium text-teal-anchor">{contact.email}</span> with any questions and your
          personalized quote. Submitting does not create an order or require payment.
        </p>
        <Link to="/covers" className="mt-8 inline-flex rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all hover:bg-teal-hover hover:-translate-y-px">Explore Other Fabrics</Link>
      </div>
    );
  }

  return (
    <div data-testid={`cover-builder-${fabric.id}`}>
      <SEO title={`${fabric.name} Custom Cover`} description={`Design your custom ${fabric.name} cushion cover — pick colors, enter measurements and request a personalized quote.`} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-10 lg:pt-24">
        <Reveal>
          <Link to="/covers" className="text-xs font-semibold uppercase tracking-[0.15em] text-teal-primary link-underline">← All fabrics</Link>
          <Eyebrow className="mt-5 block">Custom cover</Eyebrow>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.03] max-w-3xl">{fabric.name}</h1>
          <p className="mt-6 max-w-2xl text-lg text-teal-anchor/70 leading-relaxed font-light">{fabric.tagline}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Preview */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <CushionPreview length={dims.length} width={dims.width} height={dims.height} piping={piping} topFab={fabOf(topColor)} sideFab={fabOf(sideColor)} />
            <p className="mt-3 text-center text-xs uppercase tracking-[0.18em] text-teal-anchor/45">
              Live preview · {fabric.dualTone ? `${topColor.label} top / ${sideColor.label} sides` : topColor.label}
            </p>
            <div className="mt-8 aspect-[4/3] overflow-hidden rounded-sm bg-teal-anchor/5">
              <img src={activePhoto} alt={fabric.name} className="h-full w-full object-cover" />
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {fabric.photos.map((p, i) => (
                <button key={i} data-testid={`cb-thumb-${i}`} onClick={() => setActivePhoto(p)} className={`aspect-square overflow-hidden rounded-sm border transition-colors ${activePhoto === p ? "border-teal-primary" : "border-transparent hover:border-teal-anchor/30"}`}>
                  <img src={p} alt={`Sample ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Configurator */}
          <div className="space-y-8">
            {/* Colors */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">1. {fabric.dualTone ? "Choose your suedes" : "Choose your color"}</Label>
              {fabric.dualTone ? (
                <div className="mt-3 space-y-5">
                  <div>
                    <p className="mb-2 text-sm font-medium text-teal-anchor/70">Top &amp; Bottom</p>
                    <div className="grid grid-cols-2 gap-3">
                      {fabric.colors.map((c) => <Swatch key={c.id} color={c} active={topId === c.id} onClick={() => setTopId(c.id)} testid={`cb-top-${c.id}`} />)}
                    </div>
                  </div>
                  <div>
                    <p className="mb-2 text-sm font-medium text-teal-anchor/70">Sides (boxing)</p>
                    <div className="grid grid-cols-2 gap-3">
                      {fabric.colors.map((c) => <Swatch key={c.id} color={c} active={sideId === c.id} onClick={() => setSideId(c.id)} testid={`cb-side-${c.id}`} />)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {fabric.colors.map((c) => (
                    <Swatch key={c.id} color={c} active={topId === c.id} onClick={() => { setTopId(c.id); setSideId(c.id); }} testid={`cb-color-${c.id}`} />
                  ))}
                </div>
              )}
            </div>

            {/* Dimensions */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">2. Enter your measurements (inches)</Label>
              <div className="mt-3 grid grid-cols-3 gap-4">
                {[{ k: "length", label: "Length" }, { k: "width", label: "Width" }, { k: "height", label: "Thickness" }].map((f) => (
                  <div key={f.k}>
                    <Label htmlFor={`d-${f.k}`} className="text-xs text-teal-anchor/60">{f.label}</Label>
                    <Input id={`d-${f.k}`} data-testid={`cb-${f.k}`} inputMode="decimal" value={dims[f.k]} onChange={setDim(f.k)} placeholder="0" className="mt-1.5 rounded-sm border-teal-anchor/20 bg-paper text-lg" />
                  </div>
                ))}
              </div>
              <Textarea data-testid="cb-additional" value={additional} onChange={(e) => setAdditional(e.target.value)} rows={2} placeholder="Additional cushion sizes or shape notes (optional)" className="mt-3 rounded-sm border-teal-anchor/20 bg-paper" />
            </div>

            {/* Options */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">3. Options</Label>
              <div className="mt-3 space-y-3">
                <div className="flex items-center justify-between rounded-sm border border-teal-anchor/12 bg-white px-5 py-4">
                  <div><p className="font-medium">Contrast piping</p><p className="text-sm text-teal-anchor/55">Tailored welt cord along the edges</p></div>
                  <Switch data-testid="cb-piping" checked={piping} onCheckedChange={setPiping} />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs text-teal-anchor/60">Zipper style</Label>
                    <Select value={zipper} onValueChange={setZipper}>
                      <SelectTrigger data-testid="cb-zipper" className="mt-1.5 rounded-sm border-teal-anchor/20"><SelectValue /></SelectTrigger>
                      <SelectContent>{ZIPPER_OPTIONS.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-teal-anchor/60">Quantity</Label>
                    <div className="mt-1.5 flex items-center gap-3">
                      <button data-testid="cb-qty-minus" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="h-9 w-9 rounded-sm border border-teal-anchor/20 text-lg leading-none hover:border-teal-primary">−</button>
                      <span data-testid="cb-qty" className="w-8 text-center text-lg">{quantity}</span>
                      <button data-testid="cb-qty-plus" onClick={() => setQuantity((q) => q + 1)} className="h-9 w-9 rounded-sm border border-teal-anchor/20 text-lg leading-none hover:border-teal-primary">+</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Photos */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">4. Add photos (recommended)</Label>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="cb-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="cb-upload-btn" disabled={uploading} className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-dashed border-teal-anchor/30 bg-paper py-6 text-sm text-teal-anchor/70 transition-colors hover:border-teal-primary hover:text-teal-primary disabled:opacity-60">
                {uploading ? <><Loader2 size={18} className="animate-spin" /> Uploading…</> : <><Upload size={18} /> Upload photos of your cushion or furniture</>}
              </button>
              {photos.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2" data-testid="cb-photos">
                  {photos.map((p) => (
                    <div key={p.storage_path} className="group relative aspect-square overflow-hidden border border-teal-anchor/12">
                      <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setPhotos((x) => x.filter((y) => y.storage_path !== p.storage_path))} className="absolute right-1 top-1 rounded-full bg-teal-anchor/80 p-1 text-paper opacity-0 group-hover:opacity-100"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Contact */}
            <div>
              <Label className="text-xs uppercase tracking-[0.15em]">5. Your details</Label>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Input data-testid="cb-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Full name *" className="rounded-sm border-teal-anchor/20 bg-paper" />
                <Input data-testid="cb-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email *" className="rounded-sm border-teal-anchor/20 bg-paper" />
                <Input data-testid="cb-phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Phone (optional)" className="rounded-sm border-teal-anchor/20 bg-paper" />
                <Input data-testid="cb-zip" value={contact.zip} onChange={(e) => setContact({ ...contact, zip: e.target.value })} placeholder="Shipping ZIP (optional)" className="rounded-sm border-teal-anchor/20 bg-paper" />
              </div>
              <Textarea data-testid="cb-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything else we should know? (optional)" className="mt-4 rounded-sm border-teal-anchor/20 bg-paper" />
            </div>

            <button data-testid="cb-submit" onClick={submit} disabled={submitting || uploading} className="w-full rounded-sm bg-teal-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-paper transition-all hover:bg-teal-hover hover:-translate-y-px disabled:opacity-60">
              {submitting ? "Submitting…" : "Request My Quote"}
            </button>
            <p className="text-center text-xs text-teal-anchor/50">Submitting does not create an order or require payment. Every project is personally reviewed before we send a quote.</p>

            <ul className="grid gap-3 border-t border-teal-anchor/12 pt-6 text-sm text-teal-anchor/70 sm:grid-cols-3">
              <li className="flex items-center gap-2"><Ruler size={16} className="text-teal-primary" /> Exact custom fit</li>
              <li className="flex items-center gap-2"><Scissors size={16} className="text-teal-primary" /> Hidden zipper closure</li>
              <li className="flex items-center gap-2"><Truck size={16} className="text-teal-primary" /> Reviewed before production</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
